import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  message,
  type InputRef
} from 'antd'
import { useCallback, useEffect, useRef, type FC } from 'react'
import { useMutation } from '@tanstack/react-query'
import { apiCreateLicense, apiUpdateLicense } from '~/api/adminApi'
import { DatePicker } from '~/components/DatePicker'
import { RegionSelect } from '~/components/RegionSelect'
import { useSoftwaresQuery } from '~/pages/query'
import { type License } from '~/types/license'
import { showErrors } from '~/utils/helpers'

interface LicenseFormProps {
  onSuccess: () => void
  license: License | null
}

export const LicenseForm: FC<LicenseFormProps> = ({ onSuccess, license }) => {
  const createMutation = useMutation({
    mutationFn: apiCreateLicense,
    onError: showErrors
  })

  const updateMutation = useMutation({
    mutationFn: async (values: any) => {
      if (license == null) {
        throw new Error('Trying to update an empty license')
      }

      return await apiUpdateLicense(license.id, values)
    },
    onError: showErrors
  })

  const isUpdate = license != null
  const mutation = isUpdate ? updateMutation : createMutation
  const nameInputRef = useRef<InputRef>(null)

  const { data: softwares } = useSoftwaresQuery()

  const onSubmit = useCallback(
    (values) => {
      mutation.mutate(values, {
        onSuccess: () => {
          onSuccess()
          message.success('Created successfully')
        }
      })
    },
    [mutation, onSuccess]
  )

  useEffect(() => {
    setTimeout(() => nameInputRef.current?.focus(), 300)
  }, [])

  return (
    <Form layout='vertical' onFinish={onSubmit} initialValues={{ ...license }}>
      <Row>
        <Col span={24}>
          <Form.Item
            name='name'
            label='Name/MAC Address'
            rules={[{ required: true }]}
          >
            <Input
              placeholder='Name/MAC Address'
              disabled={isUpdate}
              ref={nameInputRef}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Form.Item
            name='region_code'
            label='Location'
            rules={[{ required: true, message: 'Please select the location' }]}
          >
            <RegionSelect disabled={isUpdate} />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Form.Item
            name='software_id'
            label='Software'
            rules={[{ required: true }]}
          >
            <Select
              placeholder='Software'
              disabled={isUpdate}
              options={softwares?.map((i) => ({
                value: i.id,
                label: i.name
              }))}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Form.Item name='availability_zone' label='Availability Zone'>
            <Input placeholder='Availability Zone' disabled={isUpdate} />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Form.Item
            name='license_key'
            label='License Key'
            rules={[{ required: true }]}
          >
            <Input.TextArea placeholder='License Key' autoSize />
          </Form.Item>
        </Col>
      </Row>
      {isUpdate && (
        <Row>
          <Col span={24}>
            <Form.Item name='expired_at' label='Expired at'>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      )}
      <Row gutter={16}>
        <Col span={24}>
          <Button
            type='primary'
            htmlType='submit'
            loading={mutation.isPending}
            disabled={mutation.isPending}
          >
            Submit
          </Button>
        </Col>
      </Row>
    </Form>
  )
}
